/**
 * Resume-draft stash (PR-ONB-RESUME-1B).
 *
 * The area "Dati" page and the wizard live in different route trees (the
 * react-hook-form instance mounts under /registrazione-intermediario). To
 * resume a draft, the area page stashes the SERVER draft here, navigates to
 * the saved step, and the RegistrationLayout consumes the stash on mount
 * (form.reset). sessionStorage + short TTL: per-tab, survives the redirect,
 * never outlives the session. Storage is injectable for unit tests.
 */

const STASH_KEY = 'ab-onboarding:resume-draft:v1';
const STASH_TTL_MS = 5 * 60 * 1000;

export interface ResumeDraftStash {
  draftPayload: Record<string, unknown>;
  draftRevision: number;
  requestPublicId: string;
  currentStep: string | null;
  /**
   * PR-ONB-PRECHECK-3 — OPEN correction issues at resume time, so the wizard
   * can render the per-step banners on NEEDS_CORRECTION. Optional (older
   * stashes / plain DRAFT resumes carry none).
   */
  openIssues?: Array<{
    id: string;
    targetType: 'FIELD' | 'DOCUMENT';
    targetKey: string;
    reasonCode: string;
    note: string | null;
    createdAt: string;
  }>;
  storedAt: number;
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function defaultStorage(): StorageLike | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function saveResumeDraftStash(
  stash: Omit<ResumeDraftStash, 'storedAt'>,
  now: number = Date.now(),
  storage: StorageLike | null = defaultStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(STASH_KEY, JSON.stringify({ ...stash, storedAt: now }));
  } catch {
    // Quota/serialization failures must never break navigation.
  }
}

/** Read AND clear (single consumer: the RegistrationLayout mount). */
export function consumeResumeDraftStash(
  now: number = Date.now(),
  storage: StorageLike | null = defaultStorage(),
): ResumeDraftStash | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(STASH_KEY);
    storage.removeItem(STASH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ResumeDraftStash;
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof parsed.draftRevision !== 'number' ||
      typeof parsed.requestPublicId !== 'string' ||
      typeof parsed.storedAt !== 'number' ||
      typeof parsed.draftPayload !== 'object' ||
      parsed.draftPayload === null
    ) {
      return null;
    }
    if (now - parsed.storedAt > STASH_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}
